'use client';
import { createContext, useCallback, useContext, useEffect, useId, useMemo, useRef, useState, useSyncExternalStore, type CSSProperties, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { AlertCircle, AlertTriangle, Check, Info } from 'lucide-react';
import { Spinner } from './states';
import { Toaster, toast as sonner, type ExternalToast } from 'sonner';
import { useTranslation } from '../../lib/i18n';
import { RUNTIME_LIMIT_BOUNDS, RUNTIME_LIMIT_DEFAULTS } from '../../lib/runtimeLimits';
import { daemonReconnecting, subscribeDaemonReconnect, isUnconfirmedWrite, WRITE_OUTCOME_UNKNOWN } from '../../lib/elowenClient';
import { assertFailureContract, assertSaveFeedback, classifyFailure, type SaveFailure, type SaveFeedback, type SaveRecoveryAction } from '../../lib/saveFeedback';
import type { AutosaveToastOptions, OperationFailureOptions } from 'elowen-plugin-ui-kit';
import { authGeneration, subscribeAuthTransitions } from '../../lib/token';
import { activeToastOutlet, hasToastScope, ROOT_TOAST_SCOPE, subscribeToastOutlets, useActiveToastScope, useOwnerHomeScope } from './overlayStack';
import { useToastScope } from './toastSurface';

type Tone = 'ok' | 'error';
/** A recovery the card offers. Plain data: no renderer or event type crosses the seam. */
export interface ToastAction {
  label: string;
  /** May reject; the card then reports that the action failed and offers the actions again. */
  run: () => void | Promise<void>;
}
interface ToastOptions {
  /** Identifies the operation: a repeated report replaces the card instead of stacking another. */
  key?: string;
  error?: unknown;
  /** Recovery actions (at most two read well). They require a `key` and act only while the surface that
   *  raised the card is the active one. */
  actions?: readonly ToastAction[];
  /** Keep the card until it is resolved, its surface closes or its key is dismissed. Needs actions: a
   *  persistent card with no action has no way out. */
  persistent?: boolean;
}
/** Everything the host adds on top of what a plugin can say. */
interface InternalToastOptions extends ToastOptions {
  scope?: string;
  /** The card's lifetime belongs to a state the owner publishes (an autosave failure): an action completing
   *  never dismisses it, the owner's state does. Cards of imperative callers end when their action resolves. */
  ownerManaged?: boolean;
  /** An imperative writer's instance key; its existing owner-home claim determines where actions may run. */
  reportedBy?: string;
}
interface PromiseMessages<T> {
  loading: string;
  success: string | ((value: T) => string);
  error: string | ((error: unknown) => string);
}
interface ToastCtx {
  toast: (message: string, tone?: Tone, options?: ToastOptions) => void;
  promise: <T>(task: Promise<T>, messages: PromiseMessages<T>, options?: ToastOptions) => Promise<T>;
  dismissKey: (key: string) => void;
}
type SaveOwnerState = 'busy' | 'saved' | 'quiet';
interface ToastFeedbackCtx extends ToastCtx {
  toast: (message: string, tone?: Tone, options?: InternalToastOptions) => void;
  /** One autosave writer's state inside a surface; the surface confirms once, when none is busy. */
  setSaveState: (scope: string, owner: string, state: SaveOwnerState, attempt: number) => void;
  forgetSaveOwner: (scope: string, owner: string) => void;
  reportFailure: (key: string, scope: string, failure: SaveFailure, ownerManaged: boolean) => void;
}
const Ctx = createContext<ToastFeedbackCtx | null>(null);
const DEDUPE_MS = 4000;
const TABBABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
const SAVED_DELAY_MS = 1500;
const DEFAULT_TOAST_MS = RUNTIME_LIMIT_DEFAULTS.toastDurationMs;
const [MIN_TOAST_MS, MAX_TOAST_MS] = RUNTIME_LIMIT_BOUNDS.toastDurationMs;

/** Shared runtime limits also cover the period before the shell's config query settles. */
export function resolveToastDuration(limits?: { toastDurationMs?: number }): number {
  const value = limits?.toastDurationMs;
  return typeof value === 'number' && Number.isFinite(value)
    ? Math.min(MAX_TOAST_MS, Math.max(MIN_TOAST_MS, value))
    : DEFAULT_TOAST_MS;
}

type ShowLevel = 'success' | 'error' | 'info';
interface ActionEntry {
  key: string;
  level: ShowLevel;
  actions: readonly ToastAction[];
  persistent: boolean;
  scope: string;
  generation: number;
  pending: boolean;
  ownerManaged: boolean;
  reportedBy?: string;
}
interface ActionStore {
  get: (id: string | number) => ActionEntry | undefined;
  subscribe: (listener: () => void) => () => void;
  run: (id: string | number, action: ToastAction) => void;
}

/** The actions of one card: one group owned by the adapter, because Sonner's action and cancel slots
 *  differ in what they dismiss and cannot express an asynchronous second action. It shows only while the
 *  surface that raised the card is the active one, and disables itself while an action is running. */
function ToastActions({ store, id }: { store: ActionStore; id: string | number }) {
  const entry = useSyncExternalStore(store.subscribe, () => store.get(id), () => undefined);
  const activeScope = useActiveToastScope();
  const scope = useOwnerHomeScope(entry?.reportedBy ?? '', entry?.scope ?? ROOT_TOAST_SCOPE);
  if (!entry || scope !== activeScope) return null;
  return (
    <div data-slot="toast-actions" data-toast-id={String(id)} className="mt-2 flex flex-wrap gap-2">
      {entry.actions.map((action) => (
        <button
          key={action.label}
          type="button"
          disabled={entry.pending}
          aria-busy={entry.pending || undefined}
          onClick={() => store.run(id, action)}
          className="min-h-9 rounded-md border border-background/40 px-3 text-ui text-background outline-none hover:bg-background/15 focus-visible:ring-3 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-60 pointer-coarse:min-h-11"
        >
          {action.label}
        </button>
      ))}
    </div>
  );
}

/** Move focus from a toast to a control outside the dock. Sonner hands focus back to whatever it saw before
 *  the list was entered when focus leaves the list, so the first attempt can lose; by then it has let go,
 *  and the second lands. */
function focusOutOfDock(target: HTMLElement | null | undefined): void {
  if (!target) return;
  target.focus({ preventScroll: true });
  if (document.activeElement !== target) target.focus({ preventScroll: true });
}

interface SaveGroup {
  busy: Set<string>;
  confirm: Map<string, number>;
  announced: Map<string, number>;
}

/** The sole Sonner adapter, and the one owner of where its dock lives. Its callbacks stay stable across
 * announcements and config changes.
 *
 * The dock is ONE element that this provider creates and keeps: a stable portal container for the single
 * Toaster. While only the page is up it is a child of <body>, exempt from both modal isolation sweeps. While a
 * modal layer is up it is moved into that layer's toast outlet (see overlayStack.ts), i.e. into the real
 * focus scope, so toast actions are reachable by Tab, Shift+Tab and Alt+T without touching the trap. The move
 * is a DOM reparent of the container; the Toaster, its store and every keyed card stay mounted. */
export function ToastProvider({ children, durationMs = DEFAULT_TOAST_MS }: { children: ReactNode; durationMs?: number }) {
  const { t } = useTranslation();
  const reconnecting = useSyncExternalStore(subscribeDaemonReconnect, daemonReconnecting, () => false);
  const reconnectMessage = useRef(t.common.daemonReconnecting);
  reconnectMessage.current = t.common.daemonReconnecting;
  const toasterId = useId();
  const [dock, setDock] = useState<HTMLElement | null>(null);
  const sequence = useRef(0);
  const owned = useRef(new Set<string | number>());
  const keys = useRef(new Map<string, string | number>());
  const recent = useRef(new Map<string, number>());
  const confirmations = useRef(new Map<string, ReturnType<typeof setTimeout>>());
  const groups = useRef(new Map<string, SaveGroup>());
  const entries = useRef(new Map<string | number, ActionEntry>());
  const refresh = useRef<{ id: string; generation: number; previousGeneration: number } | null>(null);
  const entryListeners = useRef(new Set<() => void>());
  const scopeKeys = useRef(new Map<string, Set<string>>());
  const lastOutside = useRef<HTMLElement | null>(null);
  const active = useRef(true);
  const text = useRef(t.common);
  text.current = t.common;
  const duration = useRef(durationMs);
  duration.current = durationMs;
  const showRef = useRef<(message: string, level: ShowLevel, opts?: InternalToastOptions) => void>(() => {});

  const notifyEntries = useCallback(() => { for (const listener of Array.from(entryListeners.current)) listener(); }, []);
  /** Focus that was inside a toast must land somewhere valid when the toast goes: the control that had it
   *  before, else the active surface. Never the page behind a modal. */
  const restoreFocus = useCallback(() => {
    const current = document.activeElement;
    if (current && current !== document.body && !current.closest('[data-slot="sonner-dock"]')) return;
    const surface = activeToastOutlet()?.node.parentElement ?? null;
    const previous = lastOutside.current;
    const usable = previous !== null && previous.isConnected && !previous.inert && !previous.closest('[inert]')
      && (surface === null || surface.contains(previous));
    focusOutOfDock(usable ? previous : surface);
  }, []);

  const dismissKey = useCallback((key: string) => {
    const pending = confirmations.current.get(key);
    if (pending !== undefined) clearTimeout(pending);
    confirmations.current.delete(key);
    const id = keys.current.get(key);
    if (id !== undefined) {
      // A card that goes while one of its actions holds focus must not strand the keyboard.
      const holdsFocus = document.activeElement?.closest('[data-slot="toast-actions"]')?.getAttribute('data-toast-id') === String(id);
      sonner.dismiss(id);
      owned.current.delete(id);
      keys.current.delete(key);
      if (entries.current.delete(id)) notifyEntries();
      if (holdsFocus) restoreFocus();
    }
  }, [notifyEntries, restoreFocus]);
  const optionsFor = useCallback((opts: InternalToastOptions = {}, keyed = true): ExternalToast => {
    const id = keyed && opts.key ? keys.current.get(opts.key) ?? `${toasterId}:${++sequence.current}` : `${toasterId}:${++sequence.current}`;
    if (keyed && opts.key) keys.current.set(opts.key, id);
    owned.current.add(id);
    const forget = () => {
      owned.current.delete(id);
      if (entries.current.delete(id)) notifyEntries();
      if (opts.key && keys.current.get(opts.key) === id) keys.current.delete(opts.key);
    };
    return { id, toasterId, duration: duration.current, onDismiss: forget, onAutoClose: forget };
  }, [notifyEntries, toasterId]);

  useEffect(() => subscribeAuthTransitions((transition) => {
    const generation = authGeneration();
    if (!transition.refresh) { refresh.current = null; return; }
    if (transition.phase === 'start') {
      // token.ts advances the request generation before publishing each transition. Only cards of
      // the immediately preceding credential may survive this SAME-account refresh.
      refresh.current = {
        id: transition.id, generation, previousGeneration: generation - 1,
      };
      return;
    }
    const pending = refresh.current;
    if (!pending || pending.id !== transition.id) return;
    if (transition.phase !== 'ready') {
      if (generation !== pending.generation + 1) { refresh.current = null; return; }
      pending.generation = generation;
      return;
    }
    // A commit is not identity proof. LoginGate alone publishes ready after its fresh /auth/me.
    // An intervening login/logout generation must never resurrect the old account's callbacks.
    if (generation === pending.generation) {
      for (const entry of entries.current.values()) {
        // Generation is not rendered; preserve the run object's identity for an action already in flight.
        if (entry.generation >= pending.previousGeneration && entry.generation <= generation) entry.generation = generation;
      }
    }
    refresh.current = null;
  }), []);

  const store = useMemo<ActionStore>(() => ({
    get: (id) => entries.current.get(id),
    subscribe: (listener) => { entryListeners.current.add(listener); return () => { entryListeners.current.delete(listener); }; },
    run: (id, action) => {
      const entry = entries.current.get(id);
      if (!entry || entry.pending || refresh.current?.generation === authGeneration()) return;
      // A card raised under another account must not run that account's callbacks.
      if (entry.generation !== authGeneration()) { dismissKey(entry.key); return; }
      // This object IS the run: whatever replaces the card meanwhile (a newer failure of the same key) is a
      // different entry, and the completion of an older action must never touch it.
      const running = { ...entry, pending: true };
      entries.current.set(id, running);
      notifyEntries();
      void (async () => {
        try {
          await action.run();
        } catch {
          if (entries.current.get(id) !== running) return;
          // The same card reports the failed action and keeps every action available.
          showRef.current(text.current.actionFailed, entry.level === 'info' ? 'info' : 'error', {
            key: entry.key, scope: entry.scope, actions: entry.actions, persistent: entry.persistent, ownerManaged: entry.ownerManaged, reportedBy: entry.reportedBy,
          });
          return;
        }
        if (entries.current.get(id) !== running) return;
        if (entry.ownerManaged) {
          // The owner's own state decides whether the failure is over: a retry that failed again resolves too.
          entries.current.set(id, { ...running, pending: false });
          notifyEntries();
          return;
        }
        dismissKey(entry.key);
      })();
    },
  }), [dismissKey, notifyEntries]);

  const show = useCallback((message: string, level: ShowLevel, opts: InternalToastOptions = {}) => {
    if (!active.current) return;
    if (opts.actions?.length && !opts.key) throw new Error('Toast actions require a key');
    if (opts.persistent && !opts.actions?.length) throw new Error('A persistent toast needs at least one action');
    const scope = opts.scope ?? ROOT_TOAST_SCOPE;
    const options = optionsFor(opts);
    const id = options.id!;
    let description: ReactNode;
    const persistent = opts.persistent === true;
    if (opts.actions?.length && opts.key) {
      entries.current.set(id, { key: opts.key, level, actions: opts.actions, persistent, scope, generation: authGeneration(), pending: false, ownerManaged: opts.ownerManaged === true, reportedBy: opts.reportedBy });
      notifyEntries();
      description = <ToastActions store={store} id={id} />;
    } else if (entries.current.delete(id)) notifyEntries();
    if (opts.key && scope !== ROOT_TOAST_SCOPE) {
      const bucket = scopeKeys.current.get(scope) ?? new Set<string>();
      bucket.add(opts.key);
      scopeKeys.current.set(scope, bucket);
    }
    const shown: ExternalToast = {
      ...options,
      description,
      duration: persistent ? Infinity : duration.current,
      dismissible: !persistent,
    };
    if (level === 'success') sonner.success(message, shown);
    else if (level === 'info') sonner.info(message, shown);
    else sonner.error(message, shown);
  }, [notifyEntries, optionsFor, store]);
  showRef.current = show;

  const toast = useCallback((message: string, tone: Tone = 'ok', opts: InternalToastOptions = {}) => {
    if (!active.current) return;
    if (tone === 'error' && (isUnconfirmedWrite(opts.error) || message === 'daemon_restarting' || message === WRITE_OUTCOME_UNKNOWN)) {
      show(text.current.saveUnconfirmed, 'info', opts);
      return;
    }
    if (tone === 'error') {
      // A keyed failure replaces its operation's card in place, so a success it supersedes never lingers.
      if (opts.key) {
        const pending = confirmations.current.get(opts.key);
        if (pending !== undefined) clearTimeout(pending);
        confirmations.current.delete(opts.key);
      }
      show(message, 'error', opts);
      return;
    }
    if (!opts.key) {
      const now = Date.now();
      for (const [key, time] of recent.current) if (now - time >= DEDUPE_MS) recent.current.delete(key);
      const identity = `${tone}:${message}`;
      const previous = recent.current.get(identity);
      if (previous !== undefined && now - previous < DEDUPE_MS) return;
      recent.current.set(identity, now);
    }
    show(message, 'success', opts);
  }, [show]);

  const promise = useCallback(async <T,>(task: Promise<T>, messages: PromiseMessages<T>, opts: ToastOptions = {}): Promise<T> => {
    if (!active.current) return task;
    const options = optionsFor(opts);
    sonner.loading(messages.loading, { ...options, duration: Infinity, dismissible: false });
    try {
      const value = await task;
      if (active.current && owned.current.has(options.id!)) {
        sonner.success(typeof messages.success === 'function' ? messages.success(value) : messages.success, { ...options, dismissible: true });
      }
      return value;
    } catch (error) {
      if (active.current && owned.current.has(options.id!)) {
        // A keyed operation reports its failure on its own card; an unkeyed one gets a fresh card so it
        // never overwrites or deduplicates another error.
        if (!opts.key) { sonner.dismiss(options.id); owned.current.delete(options.id!); }
        toast(typeof messages.error === 'function' ? messages.error(error) : messages.error, 'error', { ...opts, error });
      }
      throw error;
    }
  }, [optionsFor, toast]);

  const evaluateGroup = useCallback((scope: string, group: SaveGroup, added: boolean) => {
    const key = `saved:${scope}`;
    const pending = confirmations.current.get(key);
    if (group.busy.size > 0 || group.confirm.size === 0) {
      if (pending !== undefined) { clearTimeout(pending); confirmations.current.delete(key); }
      return;
    }
    if (pending !== undefined && !added) return;
    if (pending !== undefined) clearTimeout(pending);
    // One trailing confirmation per surface, after the whole group has settled: every field of a form or
    // dialog feeds the SAME keyed card, which updates in place instead of stacking.
    confirmations.current.set(key, setTimeout(() => {
      confirmations.current.delete(key);
      for (const [owner, attempt] of group.confirm) group.announced.set(owner, attempt);
      group.confirm.clear();
      toast(text.current.savedToast, 'ok', { key, scope });
    }, SAVED_DELAY_MS));
  }, [toast]);
  const setSaveState = useCallback((scope: string, owner: string, state: SaveOwnerState, attempt: number) => {
    if (!active.current) return;
    let group = groups.current.get(scope);
    if (!group) { group = { busy: new Set(), confirm: new Map(), announced: new Map() }; groups.current.set(scope, group); }
    // The first look at an owner is its initial snapshot: whatever it already settled is never announced.
    if (!group.announced.has(owner)) group.announced.set(owner, attempt);
    group.busy.delete(owner);
    group.confirm.delete(owner);
    let added = false;
    if (state === 'busy') group.busy.add(owner);
    else if (state === 'saved' && group.announced.get(owner) !== attempt) { group.confirm.set(owner, attempt); added = true; }
    evaluateGroup(scope, group, added);
  }, [evaluateGroup]);
  const forgetSaveOwner = useCallback((scope: string, owner: string) => {
    const group = groups.current.get(scope);
    if (!group) return;
    group.busy.delete(owner);
    group.confirm.delete(owner);
    group.announced.delete(owner);
    evaluateGroup(scope, group, false);
    if (group.announced.size === 0) groups.current.delete(scope);
  }, [evaluateGroup]);

  const reportFailure = useCallback((key: string, scope: string, failure: SaveFailure, ownerManaged: boolean) => {
    if (failure.kind === 'validation') return;
    // A conflict or an unconfirmed write with no way out would raise a card that only expires: refuse it.
    assertFailureContract(failure);
    const copy = text.current;
    // Autosaves already resolve their resource claim in the observer. Imperative operations name their
    // writer with the operation key; action rendering follows that live claim without replaying the card.
    const reportedBy = ownerManaged ? undefined : failure.owner;
    const recovery: { retry?: SaveRecoveryAction; reload?: SaveRecoveryAction; merge?: SaveRecoveryAction; leave?: SaveRecoveryAction } = failure.recovery;
    const actions: ToastAction[] = [];
    if (failure.kind === 'conflict') {
      if (recovery.reload) actions.push({ label: copy.reloadChanges, run: recovery.reload });
      if (recovery.merge) actions.push({ label: copy.keepMyChanges, run: recovery.merge });
      toast(failure.message ?? copy.saveConflict, 'error', { key, scope, error: failure.cause, actions, persistent: true, ownerManaged, reportedBy });
      return;
    }
    if (failure.kind === 'unconfirmed') {
      // Reloading verifies; leaving does not, and is labelled as the abandonment it is.
      actions.push(recovery.reload ? { label: copy.reloadChanges, run: recovery.reload } : { label: copy.close, run: recovery.leave! });
      show(failure.message ?? copy.saveUnconfirmed, 'info', { key, scope, actions, persistent: true, ownerManaged, reportedBy });
      return;
    }
    if (recovery.retry) actions.push({ label: copy.retry, run: recovery.retry });
    toast(failure.message ?? copy.saveFailed, 'error', { key, scope, error: failure.cause, ownerManaged, reportedBy, ...(actions.length ? { actions } : {}) });
  }, [show, toast]);

  // The provider creates the dock once and keeps it for its whole life.
  useEffect(() => {
    active.current = true;
    const node = document.createElement('div');
    node.dataset.slot = 'sonner-dock';
    node.setAttribute('data-overlay-exempt', '');
    node.setAttribute('aria-live', 'off');
    document.body.appendChild(node);
    // Phone outlets reserve room below the header; desktop outlets are viewport-fixed. CSS owns that
    // breakpoint. Sonner stacks out of flow, so expose its measured stack height without forcing a layout.
    let fitFrame = 0;
    const fit = () => {
      fitFrame = 0;
      const outlet = node.parentElement?.dataset.slot === 'toast-outlet' ? node.parentElement : null;
      if (!outlet) return;
      let stack = 0;
      for (const card of node.querySelectorAll<HTMLElement>('[data-sonner-toast][data-visible="true"]:not([data-removed="true"])')) {
        stack = Math.max(stack, (Number.parseFloat(card.style.getPropertyValue('--offset')) || 0) + card.offsetHeight);
      }
      outlet.style.setProperty('--toast-stack-height', stack > 0 ? `calc(var(--toast-offset-top) + ${stack}px + var(--spacing) * 2)` : '0px');
    };
    const scheduleFit = () => { if (!fitFrame) fitFrame = requestAnimationFrame(fit); };
    const observer = new MutationObserver(scheduleFit);
    observer.observe(node, { subtree: true, childList: true, attributes: true, attributeFilter: ['style', 'data-visible', 'data-removed', 'data-expanded'] });
    window.addEventListener('resize', scheduleFit);
    const place = () => {
      const target = activeToastOutlet()?.node ?? document.body;
      if (node.parentNode !== target) {
        // The outlet the dock leaves holds nothing any more.
        if (node.parentElement?.dataset.slot === 'toast-outlet') node.parentElement.style.removeProperty('--toast-stack-height');
        const focused = node.contains(document.activeElement) ? document.activeElement as HTMLElement : null;
        target.appendChild(node);
        // Moving a node blurs what it contains; hand focus straight back.
        focused?.focus({ preventScroll: true });
      }
      fit();
      // A surface that closed takes its actionable cards with it: their callbacks belong to editors that no longer exist.
      for (const scope of Array.from(scopeKeys.current.keys())) {
        if (hasToastScope(scope)) continue;
        for (const key of scopeKeys.current.get(scope) ?? []) dismissKey(key);
        scopeKeys.current.delete(scope);
        groups.current.delete(scope);
      }
    };
    const unsubscribe = subscribeToastOutlets(place);
    const track = (event: FocusEvent) => {
      if (event.target instanceof HTMLElement && !node.contains(event.target)) lastOutside.current = event.target;
    };
    const onDockKey = (event: KeyboardEvent) => {
      // Escape typed inside a toast leaves the toast, not the surface it is about (the surface ignores it).
      if (event.key === 'Escape') {
        const outside = lastOutside.current;
        if (outside?.isConnected && !outside.inert && !outside.closest('[inert]')) focusOutOfDock(outside);
        else focusOutOfDock(activeToastOutlet()?.node.parentElement);
        return;
      }
      // The surface's focus scope loops Tab from its own handler, which a keystroke typed in the dock never
      // reaches (the dock is React-parented to the provider). Close the loop here: past the last action,
      // focus returns to the surface's first control.
      if (event.key !== 'Tab' || event.shiftKey) return;
      const surface = activeToastOutlet()?.node.parentElement;
      if (!surface) return;
      const stops = Array.from(node.querySelectorAll<HTMLElement>(TABBABLE)).filter((stop) => !stop.closest('[inert]'));
      if (stops.at(-1) !== document.activeElement) return;
      const first = Array.from(surface.querySelectorAll<HTMLElement>(TABBABLE)).find((stop) => !node.contains(stop) && !stop.closest('[inert]'));
      if (!first) return;
      event.preventDefault();
      focusOutOfDock(first);
    };
    document.addEventListener('focusin', track);
    node.addEventListener('keydown', onDockKey);
    place();
    setDock(node);
    const ids = owned.current;
    const keyed = keys.current;
    const seen = recent.current;
    const pending = confirmations.current;
    const stored = entries.current;
    const buckets = scopeKeys.current;
    const grouped = groups.current;
    return () => {
      active.current = false;
      unsubscribe();
      observer.disconnect();
      window.removeEventListener('resize', scheduleFit);
      if (fitFrame) cancelAnimationFrame(fitFrame);
      document.removeEventListener('focusin', track);
      node.removeEventListener('keydown', onDockKey);
      node.remove();
      setDock(null);
      for (const id of ids) sonner.dismiss(id);
      ids.clear();
      keyed.clear();
      seen.clear();
      stored.clear();
      buckets.clear();
      grouped.clear();
      for (const timer of pending.values()) clearTimeout(timer);
      pending.clear();
    };
  }, [dismissKey]);
  useEffect(() => { dock?.toggleAttribute('data-daemon-reconnecting', reconnecting); }, [dock, reconnecting]);

  useEffect(() => {
    if (!reconnecting) { dismissKey('daemon-reconnecting'); return; }
    const options = optionsFor({ key: 'daemon-reconnecting' });
    sonner.loading(reconnectMessage.current, { ...options, duration: Infinity, dismissible: false });
    return () => { dismissKey('daemon-reconnecting'); };
  }, [reconnecting, dismissKey, optionsFor]);

  const ctx = useMemo(() => ({ toast, promise, dismissKey, setSaveState, forgetSaveOwner, reportFailure }), [toast, promise, dismissKey, setSaveState, forgetSaveOwner, reportFailure]);

  return <Ctx.Provider value={ctx}>
    {children}
    {dock && createPortal(
      <>
        {/* Sonner's own toaster is the ONLY live region for a toast: its `<section>` is
            `aria-live="polite"` with `aria-relevant="additions text"`, so every card is announced once, when
            it appears or its text changes. A second `role="alert"` mirror of the newest error message used to
            sit here and announced the same card twice (and inside a dialog it made `role="alert"` name both
            the real inline alert and this empty region). One announcement per toast, from the toaster. */}
        <Toaster id={toasterId} position="top-center" expand visibleToasts={4} gap={10} duration={duration.current}
          closeButton={false} hotkey={['altKey', 'KeyT']} containerAriaLabel={t.common.notification}
          className="!z-[var(--z-toast)]" style={{ '--width': 'calc(var(--spacing) * 120)' } as CSSProperties}
          // Sonner's phone layout starts at 600px, the app's at 767px: both offsets read the token that
          // follows the app breakpoint, so the island stays below the phone top bar. A modal surface's
          // outlet states its own value, which this var() then picks up.
          offset={{ top: 'var(--toast-offset-top)' }}
          mobileOffset={{ top: 'var(--toast-offset-top)', left: 0, right: 0 }}
          icons={{
            success: <Check size={22} className="text-success" aria-hidden />,
            error: <AlertCircle size={22} className="text-destructive" aria-hidden />,
            info: <Info size={22} className="text-info" aria-hidden />,
            warning: <AlertTriangle size={22} className="text-warning" aria-hidden />,
            loading: <Spinner size="toast" tone="text-info" className="motion-reduce:animate-none" />,
          }}
          toastOptions={{ unstyled: true, closeButtonAriaLabel: t.common.dismiss, classNames: {
            toast: 'toast-island pointer-events-auto left-0 right-0 mx-auto !w-fit max-w-[calc(100%-var(--spacing)*6)] min-w-[min(--spacing(80),calc(100%-var(--spacing)*6))] min-h-12 rounded-2xl flex items-center gap-3 border border-border bg-foreground py-3 pl-4 pr-5 text-background',
            icon: 'relative flex size-5.5 shrink-0 items-center justify-center',
            content: 'flex min-w-0 flex-col',
            title: 'text-ui',
            description: 'text-ui',
          } }} />
      </>, dock)}
  </Ctx.Provider>;
}

/** Report one writer's feedback through the toast seam. The surface it is called under (dialog, drawer, page
 * overlay) owns the grouping: every writer of that surface feeds ONE saved card, confirmed once after the
 * whole group settled and updated in place by later saves. Initial snapshots, no-ops (the attempt does not
 * move), a queued edit, pending activation, rejected values and unconfirmed writes never announce success.
 * Failures follow the decision table by kind: validation stays with the field, a conflict and an
 * unconfirmed write get one persistent recovery card limited to the recoveries the owner really has, any
 * other failure one error card with retry. A second failed attempt replaces the card; the same attempt
 * re-rendered does not. The card of a failure ends only when the owner's state says the failure is over.
 * Call it inside the surface it reports for. Feedback must be complete (`SaveFeedback`) and a provider must
 * exist: both fail loudly. */
export function useAutosaveToast(feedback: SaveFeedback, options: AutosaveToastOptions = {}): void {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useAutosaveToast must be used within ToastProvider');
  assertSaveFeedback(feedback);
  const { resource = '', success = true } = options;
  const scope = useOwnerHomeScope(resource, useToastScope());
  const instance = useId();
  const owner = `${instance}:${resource}`;
  const { status, editing, attempt, failures } = feedback;
  const latest = useRef(feedback);
  latest.current = feedback;
  const reported = useRef(new Map<string, number>());
  const failureSignature = failures.map((failure) => `${failure.owner}:${failure.attempt}:${failure.kind}`).join('|');

  useEffect(() => {
    const state: SaveOwnerState = status === 'saving' || editing ? 'busy'
      : status === 'saved' && success ? 'saved' : 'quiet';
    ctx.setSaveState(scope, owner, state, attempt);
  }, [attempt, ctx, editing, owner, scope, status, success]);

  useEffect(() => {
    const cardKey = (failureOwner: string) => `failure:${scope}:${owner}:${failureOwner}`;
    const live = new Set<string>();
    for (const failure of failures) {
      live.add(failure.owner);
      if (failure.kind === 'validation' || reported.current.get(failure.owner) === failure.attempt) continue;
      reported.current.set(failure.owner, failure.attempt);
      // Recovery is read when it is used, never captured: a card must not run an editor's stale callback.
      const lazy = (capability: 'retry' | 'reload' | 'merge' | 'leave') => failure.recovery[capability]
        ? () => latest.current.failures.find((candidate) => candidate.owner === failure.owner)?.recovery[capability]?.()
        : undefined;
      ctx.reportFailure(cardKey(failure.owner), scope, { ...failure, recovery: { retry: lazy('retry'), reload: lazy('reload'), merge: lazy('merge'), leave: lazy('leave') } } as SaveFailure, true);
    }
    for (const failureOwner of Array.from(reported.current.keys())) {
      if (live.has(failureOwner)) continue;
      reported.current.delete(failureOwner);
      ctx.dismissKey(cardKey(failureOwner));
    }
    // `failures` is read through its signature: a new array with the same attempts is not a new report.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx, failureSignature, owner, scope]);

  // Teardown: the owner's pending confirmation and failure cards must not outlive the editor.
  useEffect(() => {
    const cards = reported.current;
    return () => {
      ctx?.forgetSaveOwner(scope, owner);
      for (const failureOwner of cards.keys()) ctx?.dismissKey(`failure:${scope}:${owner}:${failureOwner}`);
      cards.clear();
    };
  }, [ctx, owner, scope]);
}

/** Report the failure of an operation a surface awaits (a confirmation, a selection save), as the one card of
 * `key`, classified by the same rules as a save controller's failures (`classifyFailure`): a `SaveConflictError`
 * is a conflict with the recoveries it carries, a lost connection an unconfirmed write with `reload`, anything
 * else an error card with the owner's curated `message`. Raw exception prose never reaches the screen and no
 * retry is invented. The cards end with the component that called this hook (the dialog closing). Call it
 * inside the surface it reports for, or have that surface claim the operation key with reportedBy; the
 * action group follows that live owner-home claim without recreating or re-announcing the card. Published as
 * `hooks.useOperationFailure`; whether the answer stays held is `holdsAnswer` of the same error. */
export function useOperationFailure(): (key: string, error: unknown, options: OperationFailureOptions) => void {
  const ctx = useContext(Ctx);
  const scope = useToastScope();
  if (!ctx) throw new Error('useOperationFailure must be used within ToastProvider');
  const raised = useRef(new Set<string>());
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    const keys = raised.current;
    return () => {
      mounted.current = false;
      for (const key of keys) ctx.dismissKey(key);
      keys.clear();
    };
  }, [ctx]);
  return useCallback((key, error, options) => {
    // A command may settle after navigation or dialog dismissal; its dead owner has no recovery UI.
    if (!mounted.current) return;
    if (typeof options?.reload !== 'function' && typeof options?.leave !== 'function') throw new Error('An operation failure needs the operation\'s reload or, without one, leave.');
    const classified = classifyFailure(error, options);
    raised.current.add(key);
    ctx.reportFailure(key, scope, { owner: key, attempt: 0, cause: error, ...classified } as SaveFailure, false);
  }, [ctx, scope]);
}

/** The imperative adapter, bound to the surface it is called under: a card with actions raised from a dialog
 * acts only while that dialog is the active surface and disappears with it. A card with actions also ends with
 * the component that raised it, because its callbacks belong to that component: raise one from a component
 * that outlives the action. Plain cards are unaffected. */
export function useToast(): ToastCtx {
  const ctx = useContext(Ctx);
  const scope = useToastScope();
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  const actionable = useRef(new Set<string>());
  useEffect(() => {
    const keys = actionable.current;
    return () => { for (const key of keys) ctx.dismissKey(key); keys.clear(); };
  }, [ctx]);
  return useMemo<ToastCtx>(() => ({
    toast: (message, tone, options) => {
      if (options?.actions?.length && options.key) actionable.current.add(options.key);
      ctx.toast(message, tone, { ...options, scope });
    },
    promise: (task, messages, options) => ctx.promise(task, messages, { ...options, scope } as ToastOptions),
    dismissKey: ctx.dismissKey,
  }), [ctx, scope]);
}
